import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import {
  LogOut,
  Music,
  Calendar,
  HelpCircle,
  Users,
  Dumbbell,
  Scale,
  Library,
  TrendingUp,
  UserCheck,
  Sun,
  Moon,
  Monitor,
  Star,
  Target,
  Flame,
  PanelLeftClose,
  PanelLeft,
  Settings,
} from "lucide-react";
import { useThemeMode, type ThemeMode } from "@/hooks/useTheme";
import { usePendingRecommendations } from "@/hooks/usePendingRecommendations";
import { useAuth } from "@/hooks/useAuth";

interface AppSidebarProps {
  isAdmin?: boolean;
  isCoach?: boolean;
  hasCoach?: boolean;
}

export function AppSidebar({ isAdmin = false, isCoach = false, hasCoach = false }: AppSidebarProps) {
  const [location] = useLocation();
  const { themeMode, setThemeMode, mounted } = useThemeMode();
  const { pendingCount } = usePendingRecommendations();
  const { toggleSidebar, state } = useSidebar();
  const { signOut } = useAuth();
  const isCollapsed = state === "collapsed";

  const cycleTheme = () => {
    if (themeMode === 'light') setThemeMode('dark');
    else if (themeMode === 'dark') setThemeMode('auto');
    else setThemeMode('light');
  };

  const getThemeIcon = () => {
    if (themeMode === 'light') return <Sun className="w-4 h-4" />;
    if (themeMode === 'dark') return <Moon className="w-4 h-4" />;
    return <Monitor className="w-4 h-4" />;
  };

  const getThemeLabel = () => {
    if (themeMode === 'light') return 'Light Mode';
    if (themeMode === 'dark') return 'Dark Mode';
    return 'Auto';
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-border/50">
      <SidebarHeader className="p-1.5">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Home" className="hover:bg-accent h-8">
              <Link href="/">
                <Flame className="w-4 h-4 text-primary" />
                <span className="font-bold text-sm">myFit<span className="text-primary">Beats</span></span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent className="overflow-hidden flex-1">
        {/* Primary Navigation */}
        <SidebarGroup className="py-0">
          <SidebarGroupContent>
            <SidebarMenu className="gap-0.5">
              {/* Calendar */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location === "/calendar"}
                  tooltip="Calendar"
                  size="sm"
                >
                  <Link href="/calendar">
                    <Calendar className="w-4 h-4" />
                    <span>Calendar</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* My Progress */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location === "/progress"}
                  tooltip="My Progress"
                  size="sm"
                >
                  <Link href="/progress">
                    <TrendingUp className="w-4 h-4" />
                    <span>My Progress</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* My Beats */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location === "/music"}
                  tooltip="My Beats"
                  size="sm"
                >
                  <Link href="/music">
                    <Music className="w-4 h-4" />
                    <span>My Beats</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* My Coach - only shown if user has a coach */}
              {hasCoach && (
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={location === "/my-coach"}
                    tooltip="My Coach"
                    size="sm"
                  >
                    <Link href="/my-coach">
                      <UserCheck className="w-4 h-4" />
                      <span>My Coach</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Training Section */}
        <SidebarGroup className="py-0 mt-0.5">
          <div className="px-3 mb-0.5 group-data-[collapsible=icon]:hidden">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60">Training</span>
          </div>
          <SidebarGroupContent>
            <SidebarMenu className="gap-0.5">
              {/* My Programs */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location === "/my-programs"}
                  tooltip="My Programs"
                  size="sm"
                >
                  <Link href="/my-programs">
                    <Library className="w-4 h-4" />
                    <span>My Programs</span>
                    {pendingCount > 0 && (
                      <Star className="w-3 h-3 text-red-500 fill-red-500 ml-1" data-testid="indicator-pending-recommendations" />
                    )}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Expert Programs */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location === "/programs"}
                  tooltip="Expert Programs"
                  size="sm"
                >
                  <Link href="/programs">
                    <Dumbbell className="w-4 h-4" />
                    <span>Expert Programs</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* My Training Blocks */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location === "/training-blocks"}
                  tooltip="My Training Blocks"
                  size="sm"
                >
                  <Link href="/training-blocks">
                    <Target className="w-4 h-4" />
                    <span>My Training Blocks</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* My Workout Preferences */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location === "/workout-preferences"}
                  tooltip="My Workout Preferences"
                  size="sm"
                >
                  <Link href="/workout-preferences">
                    <Scale className="w-4 h-4" />
                    <span>My Workout Preferences</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Admin Section - Only visible to admins */}
        {isAdmin && (
          <SidebarGroup className="py-0 mt-0.5">
            <div className="px-3 mb-0.5 group-data-[collapsible=icon]:hidden">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60">Admin</span>
            </div>
            <SidebarGroupContent>
              <SidebarMenu className="gap-0.5">
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={location === "/admin"}
                    tooltip="Admin Dashboard"
                    size="sm"
                  >
                    <Link href="/admin">
                      <Users className="w-4 h-4" />
                      <span>Admin Dashboard</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {isCoach && !isAdmin && (
          <SidebarGroup className="py-0 mt-0.5">
            <div className="px-3 mb-0.5 group-data-[collapsible=icon]:hidden">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60">Coach</span>
            </div>
            <SidebarGroupContent>
              <SidebarMenu className="gap-0.5">
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={location === "/coach"}
                    tooltip="Coach Dashboard"
                    size="sm"
                  >
                    <Link href="/coach">
                      <Users className="w-4 h-4" />
                      <span>Coach Dashboard</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      {/* Support & Account */}
      <SidebarFooter className="p-1 shrink-0">
        <SidebarMenu className="gap-0">
          {/* Theme Toggle - Expanded View */}
          <SidebarMenuItem className="group-data-[collapsible=icon]:hidden">
            <div className="flex items-center justify-center w-full px-1.5 py-0">
              {mounted && (
                <div className="flex gap-0.5 rounded-md border border-border/50 p-0.5">
                  <button
                    onClick={() => setThemeMode('light')}
                    className={`px-2 py-1 rounded transition-all ${themeMode === 'light' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}
                    data-testid="sidebar-theme-light"
                  >
                    <Sun className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setThemeMode('auto')}
                    className={`px-2 py-1 rounded transition-all ${themeMode === 'auto' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}
                    data-testid="sidebar-theme-auto"
                  >
                    <Monitor className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => setThemeMode('dark')}
                    className={`px-2 py-1 rounded transition-all ${themeMode === 'dark' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`}
                    data-testid="sidebar-theme-dark"
                  >
                    <Moon className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          </SidebarMenuItem>
          {/* Theme Button - Collapsed View */}
          {mounted && (
            <SidebarMenuItem className="hidden group-data-[collapsible=icon]:block">
              <SidebarMenuButton
                tooltip={getThemeLabel()}
                onClick={cycleTheme}
                className="text-muted-foreground hover:text-foreground"
                size="sm"
              >
                {getThemeIcon()}
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Account Settings" className="text-muted-foreground hover:text-foreground" size="sm">
              <Link href="/preferences">
                <Settings className="w-4 h-4" />
                <span>Account Settings</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="How To Use" className="text-muted-foreground hover:text-foreground" size="sm">
              <Link href="/tutorial">
                <HelpCircle className="w-4 h-4" />
                <span>How To Use</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              tooltip="Logout"
              onClick={() => signOut()}
              data-testid="sidebar-logout"
              size="sm"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem className="group-data-[collapsible=icon]:hidden">
            <SidebarMenuButton
              tooltip="Collapse Sidebar"
              onClick={toggleSidebar}
              className="text-muted-foreground hover:text-foreground"
              size="sm"
            >
              <PanelLeftClose className="w-4 h-4" />
              <span>Collapse</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          {/* Expand Button - Collapsed View */}
          <SidebarMenuItem className="hidden group-data-[collapsible=icon]:block">
            <SidebarMenuButton
              tooltip="Expand Sidebar"
              onClick={toggleSidebar}
              className="text-muted-foreground hover:text-foreground"
              size="sm"
            >
              <PanelLeft className="w-4 h-4" />
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
